import React, { useState } from "react";
import { FiX, FiDollarSign } from "react-icons/fi";
import { toast } from "react-toastify";
import { setClassLevelDefaultTuition, extractApiErrorMessage } from "../../utils/classGroupApi";
import { formatter, getPriceForTerm } from "../../utils/currencyUtils";
import { DEFAULT_ARM_LABEL, pluralize } from "../../utils/classGroupHelpers";

const TERMS = ["1", "2", "3"];

// getPriceForTerm throws on a missing price object.
const priceFor = (price, term) => Number((price && getPriceForTerm(price, term)) || 0);

// Same comparison the backend uses (exact string match per term), so the preview agrees
// with what saving actually does.
const samePrice = (a, b) =>
  Boolean(a && b) && TERMS.every((t) => String(a?.[t]) === String(b?.[t]));

// Mirrors SetClassLevelDefaultTuition: arms are compared against the previous group default
// or, the first time, against the price most arms already share.
const inferBaseline = (previousDefault, memberClasses) => {
  if (previousDefault) return previousDefault;
  const counts = new Map();
  memberClasses.forEach((cls) => {
    const price = cls.class_price;
    if (!price || typeof price !== "object") return;
    const key = TERMS.map((t) => String(price[t])).join("|");
    const existing = counts.get(key);
    counts.set(key, { price, count: (existing?.count || 0) + 1 });
  });
  if (counts.size === 0) return null;
  return Array.from(counts.values()).sort((a, b) => b.count - a.count)[0].price;
};

const SetDefaultTuitionModal = ({
  isOpen,
  onClose,
  schoolId,
  classGroupId,
  classGroupName = "Class Group",
  currentDefaultPrice = null, // { 1: string, 2: string, 3: string }
  memberClasses = [],
  subclassLabel = DEFAULT_ARM_LABEL,
  onSuccess,
}) => {
  const [price1, setPrice1] = useState(currentDefaultPrice?.["1"] || "");
  const [price2, setPrice2] = useState(currentDefaultPrice?.["2"] || "");
  const [price3, setPrice3] = useState(currentDefaultPrice?.["3"] || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const armLower = subclassLabel.toLowerCase();
  const armsPlural = pluralize(subclassLabel, 2).toLowerCase();
  const newPrice = { 1: price1, 2: price2, 3: price3 };
  const hasNewPrice = Boolean(price1 && price2 && price3);

  // Arms with no price, or on the baseline price, move to the new default; arms with a
  // different price keep it.
  const baseline = inferBaseline(currentDefaultPrice, memberClasses);
  const preview = memberClasses.map((cls) => {
    const current = cls.class_price;
    const follows = !current || (baseline !== null && samePrice(current, baseline));
    return { cls, current, status: follows ? "follows" : "custom" };
  });
  const keepCount = preview.filter((p) => p.status === "custom").length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasNewPrice) {
      setError("Enter the fees for all 3 terms");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const payload = {
        1: String(price1),
        2: String(price2),
        3: String(price3),
      };

      const res = await setClassLevelDefaultTuition(schoolId, classGroupId, payload);

      const updatedCount = res.updated_subclasses?.length || 0;
      const conflictCount = res.conflicts?.length || 0;

      toast.success(
        `Default fees saved. ${updatedCount} ${pluralize(subclassLabel, updatedCount).toLowerCase()} updated.${
          conflictCount > 0
            ? ` ${conflictCount} kept their own price.`
            : ""
        }`,
      );

      if (onSuccess) {
        onSuccess(res);
      }
      onClose();
    } catch (err) {
      console.error("Error setting default tuition:", err);
      setError(extractApiErrorMessage(err, "Failed to save the default fees"));
    } finally {
      setLoading(false);
    }
  };

  const priceInput = (id, label, value, setValue) => (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-slate-500 mb-1">
        {label}
      </label>
      <input
        type="number"
        id={id}
        required
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="25000"
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <FiDollarSign className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Set default fees</h3>
              <p className="text-xs text-slate-500">
                School fees for every {armLower} in {classGroupName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          {error && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Fees per term (₦)
            </label>
            <div className="grid grid-cols-3 gap-2.5">
              {priceInput("price_1", "1st Term", price1, setPrice1)}
              {priceInput("price_2", "2nd Term", price2, setPrice2)}
              {priceInput("price_3", "3rd Term", price3, setPrice3)}
            </div>
          </div>

          {preview.length > 0 && (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <p className="px-3.5 py-2 bg-slate-50 text-xs font-semibold text-slate-700 border-b border-slate-200">
                What happens to each {armLower}
              </p>
              <ul className="divide-y divide-slate-100">
                {preview.map(({ cls, current, status }) => (
                  <li
                    key={cls._id}
                    className="px-3.5 py-2 flex items-center justify-between gap-3 text-xs"
                  >
                    <span className="font-medium text-slate-800">{cls.class_name}</span>
                    {status === "custom" ? (
                      <span className="text-amber-700">
                        Keeps its own price ({formatter.format(priceFor(current, "1"))} 1st
                        term)
                      </span>
                    ) : (
                      <span className="text-emerald-700">
                        {hasNewPrice && samePrice(current, newPrice)
                          ? "Already on these fees"
                          : "Moves to the new fees"}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              {!currentDefaultPrice && keepCount > 0 && (
                <p className="px-3.5 py-2 bg-slate-50 text-[11px] text-slate-600 border-t border-slate-200">
                  This is the group's first default, so {armsPlural} on the most common price
                  move to it and the rest keep their own.
                </p>
              )}
              {keepCount > 0 && (
                <p className="px-3.5 py-2 bg-amber-50 text-[11px] text-amber-800 border-t border-amber-100">
                  To change a {armLower} that keeps its own price, edit that {armLower}'s fees
                  directly.
                </p>
              )}
            </div>
          )}

          <p className="text-xs text-slate-500">
            For {armsPlural} that move to the new fees, this session's unpaid invoices are
            updated to the new amount. Fully paid invoices are not changed.
          </p>

          <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !hasNewPrice}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              )}
              Save default fees
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetDefaultTuitionModal;
